/**
 * FAQsPage — Frequently Asked Questions knowledge base
 *
 * Layout:
 *  - Left sidebar: owner sections → category nav items with entry-count badges
 *  - Toolbar: inline search + "Search all FAQs" button
 *  - Main area: responsive card grid (question + answer preview)
 *  - Detail view: full in-page FAQ detail with back button, replaces the grid
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { Page, Header, Content, MarkdownContent } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useSearchParams } from 'react-router-dom';
import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import type { FaqEntry, FaqSection, FaqCategory, LinkType } from '../../types';
import { toSlug } from '../../utils';

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  // ── Layout ──
  layout: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  mainContent: { flex: 1, minWidth: 0 },

  // ── Sidebar ──
  sidebar: {
    width: 230,
    flexShrink: 0,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    position: 'sticky' as const,
    top: 0,
  },
  sidebarLabel: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
  },
  sidebarList: { padding: theme.spacing(0, 0, 1) },
  navIcon: { minWidth: 28 },
  navIconActive: { color: theme.palette.primary.main, fontSize: 18 },
  navIconInactive: { color: theme.palette.text.secondary, fontSize: 18 },
  navIconStar: { color: '#F59E0B', fontSize: 18 },

  navItem: {
    width: 'auto',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.2, 1),
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  navItemActive: {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(230,0,126,0.18)' : '#FDF0F8',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(230,0,126,0.24)' : '#FAE3F3',
    },
  },
  navText: { margin: 0, '& span': { fontSize: 13, fontWeight: 500 } },
  navTextActive: { '& span': { color: theme.palette.primary.main, fontWeight: 700 } },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    borderRadius: theme.shape.borderRadius,
    padding: '1px 7px',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#E9ECEF',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap' as const,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  badgeActive: { backgroundColor: theme.palette.primary.main, color: '#fff' },
  sectionDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    marginRight: theme.spacing(1),
    display: 'inline-block',
  },

  // ── Toolbar ──
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2.5),
    flexWrap: 'wrap' as const,
  },
  searchWrap: {
    flex: 1,
    minWidth: 160,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    height: 40,
    boxSizing: 'border-box' as const,
  },
  searchIcon: { color: theme.palette.text.secondary, fontSize: 18, flexShrink: 0 },
  searchInput: { flex: 1, fontSize: 14 },
  searchScopeGroup: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    borderRadius: theme.shape.borderRadius,
    padding: 3,
    flexShrink: 0,
    height: 40,
    boxSizing: 'border-box' as const,
  },
  searchScopeBtn: {
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none' as const,
    fontSize: 13,
    fontWeight: 600,
    padding: theme.spacing(0.4, 1.5),
    minWidth: 0,
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)',
    '&:hover': { backgroundColor: 'transparent' },
    '&.Mui-disabled': {
      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    },
  },
  searchScopeBtnActive: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    },
  },

  // ── Search results page ──
  searchResultsHeader: {
    paddingBottom: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  searchResultsCount: {
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.text.primary,
  },
  searchResultsSectionGroup: {
    marginBottom: theme.spacing(4),
  },
  searchResultsSectionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
    borderLeft: '3px solid transparent',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  searchResultsCategoryGroup: {
    marginBottom: theme.spacing(3),
    paddingLeft: theme.spacing(1),
  },
  searchResultsCategoryLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },

  // ── Section header ──
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionTitle: { fontWeight: 700, fontSize: 16 },
  sectionMeta: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: 2 },

  // ── Card grid ──
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing(1.5),
  },
  entryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'box-shadow 0.18s, transform 0.15s',
    '&:hover': { boxShadow: theme.shadows[4], transform: 'translateY(-2px)' },
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
  },
  cardTopRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.25, 0.25, 1.5),
    gap: theme.spacing(0.5),
  },
  cardIconBtn: { padding: 4 },
  cardSectionChip: {
    height: 20,
    fontSize: 10,
    fontWeight: 700,
    borderRadius: theme.shape.borderRadius,
    letterSpacing: '0.02em',
  },
  cardActionArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    padding: theme.spacing(0.5, 1.5, 1),
    gap: theme.spacing(0.75),
  },
  cardQuestion: {
    fontWeight: 700,
    fontSize: 13.5,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  cardPreview: {
    fontSize: 13,
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
  },
  tagsLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: 3,
  },
  tagsWrap: { display: 'flex', flexWrap: 'nowrap' as const, gap: 3, marginBottom: theme.spacing(1), overflow: 'hidden' },
  tagChip: { height: 18, fontSize: 10, fontWeight: 600, borderRadius: theme.shape.borderRadius },
  cardLastUpdated: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.75),
  },
  cardActionsWrap: { padding: theme.spacing(0.5, 1.5, 1.5) },
  actionBtn: {
    width: '100%',
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: 13,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.75, 0),
  },

  // ── Detail view ──
  detailBack: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
  },
  detailCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  detailCardHeader: {
    borderTop: '4px solid transparent',
    padding: theme.spacing(3),
  },
  detailCardBody: {
    padding: theme.spacing(3),
  },
  detailCardLinks: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
  },
  detailCardLinksLabel: {
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  detailTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  detailQuestion: {
    fontWeight: 700,
    fontSize: 22,
    lineHeight: 1.35,
    flex: 1,
  },
  detailSectionTitle: {
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  detailMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  detailMetaLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    marginBottom: 3,
  },
  detailMetaValue: { fontSize: 13 },
  detailTags: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap' as const,
    marginTop: theme.spacing(1.5),
  },
  answerContent: {
    fontSize: 14,
    lineHeight: 1.7,
    '& p': { margin: '0 0 8px 0' },
    '& p:last-child': { margin: 0 },
    '& a': { color: theme.palette.primary.main },
    '& strong, & b': { fontWeight: '700 !important' as any },
    '& em, & i': { fontStyle: 'italic !important' as any },
    '& ul, & ol': { paddingLeft: 20, marginBottom: 6 },
    '& li': { marginBottom: 2 },
    '& code': {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontSize: '0.875em',
      padding: '1px 4px',
      borderRadius: 3,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)',
    },
    '& pre': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      overflow: 'auto',
      marginBottom: 8,
    },
    '& pre code': { backgroundColor: 'transparent', padding: 0 },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: '0 0 8px 0',
      paddingLeft: theme.spacing(2),
      color: theme.palette.text.secondary,
    },
    '& table': { borderCollapse: 'collapse' as const, width: '100%', marginBottom: 8 },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
      textAlign: 'left' as const,
    },
    '& th': { fontWeight: 700 },
  },

  // ── Feedback ──
  feedbackSection: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  feedbackLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    flexShrink: 0,
  },
  feedbackBtn: {
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none' as const,
    fontSize: 13,
    fontWeight: 600,
    gap: theme.spacing(0.5),
  },

  // ── Related FAQs ──
  relatedSection: {
    padding: theme.spacing(2.5, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  relatedTitle: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(1),
  },
  relatedCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    transition: 'background 0.12s, box-shadow 0.12s',
    '&:hover': { backgroundColor: theme.palette.action.hover, boxShadow: theme.shadows[1] },
  },
  relatedCardQ: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    marginBottom: theme.spacing(0.5),
  },
  relatedCardMeta: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },

  // ── Empty / loading ──
  centred: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1.5),
  },

  // ── Welcome / overview page ──
  welcomeRoot: {},
  welcomeSearchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  welcomeSearchBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    height: 40,
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s',
    '&:focus-within': { borderColor: theme.palette.primary.main },
  },
  welcomeSearchIcon: { color: theme.palette.text.secondary, fontSize: 18, flexShrink: 0 },
  welcomeSearchInput: { flex: 1, fontSize: 14 },
  welcomeRecentLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  welcomeStats: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap' as const,
  },
  welcomeStatChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 20,
    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : theme.palette.grey[100],
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '& strong': { color: theme.palette.text.primary, fontWeight: 700 },
  },
}));

// ── Helpers ───────────────────────────────────────────────────────────────────

function LinkIcon({ type }: { type: LinkType }) {
  if (type === 'workflow') return <PlayArrowIcon fontSize="small" />;
  if (type === 'docs') return <MenuBookIcon fontSize="small" />;
  return <OpenInNewIcon fontSize="small" />;
}

// ── Favourites persistence ────────────────────────────────────────────────────

const FAVOURITES_KEY = 'oneadvanced:favourites:faqs:v1';

function loadFavourites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVOURITES_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function saveFavourites(set: Set<string>): void {
  localStorage.setItem(FAVOURITES_KEY, JSON.stringify([...set]));
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/** Strip markdown syntax to produce a plain-text card preview. */
function stripMarkdown(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]+`/g, '')
    .replace(/#{1,6}\s+/g, '')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/^\s*[-*+>]\s+/gm, '')
    .replace(/\n{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Find up to 4 related entries: same category first, then same section. */
function getRelated(entry: FaqEntry, allEntries: FaqEntry[]): FaqEntry[] {
  const sameCategory = allEntries.filter(
    e => e.id !== entry.id && e.categoryName === entry.categoryName,
  );
  const results = sameCategory.slice(0, 4);
  if (results.length < 4) {
    const sameSection = allEntries.filter(
      e => e.id !== entry.id && e.sectionSlug === entry.sectionSlug && e.categoryName !== entry.categoryName,
    );
    results.push(...sameSection.slice(0, 4 - results.length));
  }
  return results;
}

/** A flat nav key: "favourites" | "<sectionSlug>|<categorySlug>" */
type NavKey = string;

interface NavSection {
  section: FaqSection;
  categories: { id: string; name: string; slug: string; count: number | undefined }[];
}

function buildNavSections(
  sections: FaqSection[],
  allCategories: Record<string, FaqCategory[]>,
  categoryEntriesCache: Record<string, FaqEntry[]>,
): NavSection[] {
  return sections
    .map(sec => {
      const cats = (allCategories[sec.id] ?? [])
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(cat => {
          const count = categoryEntriesCache[cat.id]?.length ?? cat.entryCount;
          return { id: cat.id, name: cat.name, slug: toSlug(cat.name), count };
        });
      return { section: sec, categories: cats };
    })
    .filter(s => s.categories.length > 0);
}

function makeCatKey(sectionSlug: string, categoryName: string): NavKey {
  return `${sectionSlug}|${categoryName}`;
}

function getFilterPlaceholder(scope: string, key: string, sectionTitle: string): string {
  if (scope === 'global') return 'Search all FAQs…';
  if (key === 'favourites') return 'Filter favourites…';
  return `Filter in ${sectionTitle}…`;
}

// ── Main component ────────────────────────────────────────────────────────────

export function FAQsPage() {
  const { classes } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi(oneadvancedApiRef);

  // Data
  const [sections, setSections] = useState<FaqSection[]>([]);
  const [allCategories, setAllCategories] = useState<Record<string, FaqCategory[]>>({});
  const [categoryEntriesCache, setCategoryEntriesCache] = useState<Record<string, FaqEntry[]>>({});
  const [recentEntries, setRecentEntries] = useState<FaqEntry[]>([]);
  const requestedCategories = useRef(new Set<string>());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // UI
  const [searchText, setSearchText] = useState('');
  const [searchScope, setSearchScope] = useState<'filter' | 'global'>('filter');
  const [favourites, setFavourites] = useState<Set<string>>(loadFavourites);

  // Search results — populated when selectedKey === 'search'
  const [searchResultEntries, setSearchResultEntries] = useState<FaqEntry[]>([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);

  const handleToggleFavourite = useCallback((id: string) => {
    setFavourites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveFavourites(next);
      return next;
    });
  }, []);

  // Selected nav key from URL (?section=slug&category=name, or ?view=favourites)
  // The ?entry=<slug> param opens the detail view for a specific FAQ
  const selectedKey = useMemo<NavKey>(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'favourites') return 'favourites';
    if (viewParam === 'search') return 'search';
    const sectionParam = searchParams.get('section');
    const catParam = searchParams.get('category');
    if (sectionParam && catParam) return makeCatKey(sectionParam, catParam);
    return 'welcome';
  }, [searchParams]);

  const setSelectedKey = useCallback((key: NavKey) => {
    if (key === 'welcome') {
      setSearchParams({}, { replace: true });
    } else if (key === 'favourites') {
      setSearchParams({ view: 'favourites' }, { replace: true });
    } else {
      const [sectionSlug, catSlug] = key.split('|');
      setSearchParams({ section: sectionSlug, category: catSlug }, { replace: true });
    }
    setSearchText('');
    setSearchScope('filter');
  }, [setSearchParams]);

  const navigateToSearch = useCallback((q: string) => {
    const params: Record<string, string> = { view: 'search' };
    if (q.trim()) params.q = q.trim();
    setSearchParams(params, { replace: false });
    setSearchText('');
  }, [setSearchParams]);

  // Detail entry — driven by ?entry=<slug> URL param so search results can deep-link
  const entrySlug = searchParams.get('entry');
  const allCachedEntries = useMemo(
    () => Object.values(categoryEntriesCache).flat(),
    [categoryEntriesCache],
  );
  const detailEntry = useMemo(
    () => (entrySlug ? allCachedEntries.find(e => e.slug === entrySlug) ?? recentEntries.find(e => e.slug === entrySlug) ?? null : null),
    [entrySlug, allCachedEntries, recentEntries],
  );

  // Full entry — fetched on demand so list payloads can be small (answer truncated to 300 chars)
  const [fullDetailEntry, setFullDetailEntry] = useState<FaqEntry | null>(null);
  useEffect(() => {
    if (!entrySlug) {
      setFullDetailEntry(null);
      return () => {};
    }
    let cancelled = false;
    api.faq.getEntryBySlug(entrySlug)
      .then(e => { if (!cancelled) setFullDetailEntry(e); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [entrySlug, api.faq]);

  const openDetail = useCallback((entry: FaqEntry) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      next.delete('q');
      next.set('section', entry.sectionSlug);
      next.set('category', toSlug(entry.categoryName));
      next.set('entry', entry.slug);
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  const closeDetail = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('entry');
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  // Load data (sections + all category metadata on mount; entries are lazy per-section)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.faq.getSections()
      .then(fetchedSections => {
        if (cancelled) return Promise.reject(new Error('cancelled'));
        setSections(fetchedSections);
        return Promise.all(
          fetchedSections.map(s => api.faq.getCategories(s.id).then(cats => ({ sectionId: s.id, cats }))),
        );
      })
      .then(results => {
        if (cancelled) return;
        const catMap: Record<string, FaqCategory[]> = {};
        for (const { sectionId, cats } of results) catMap[sectionId] = cats;
        setAllCategories(catMap);
      })
      .catch(err => { if (!cancelled && err?.message !== 'cancelled') setLoadError(err?.message ?? 'Failed to load FAQs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Load recent entries for the welcome page in parallel
    api.faq.getRecentEntries(8).then(e => { if (!cancelled) setRecentEntries(e); }).catch(() => {});
    return () => { cancelled = true; };
  }, [api.faq]);

  // Lazy-load entries for the selected category when navigating
  useEffect(() => {
    if (selectedKey === 'welcome' || selectedKey === 'favourites' || selectedKey === 'search') return;
    const [sectionSlug, catSlug] = selectedKey.split('|');
    const section = sections.find(o => o.slug === sectionSlug);
    if (!section) return;
    const cats = allCategories[section.id];
    if (!cats) return;
    const cat = cats.find(c => toSlug(c.name) === catSlug);
    if (!cat || requestedCategories.current.has(cat.id)) return;
    requestedCategories.current.add(cat.id);
    api.faq.getEntriesByCategory(cat.id)
      .then(entries => setCategoryEntriesCache(prev => ({ ...prev, [cat.id]: entries })))
      .catch(err => setLoadError(err?.message ?? 'Failed to load entries'));
  }, [selectedKey, sections, allCategories, api.faq]);

  // Fetch search results when in search view
  useEffect(() => {
    if (selectedKey !== 'search') { setSearchResultEntries([]); return () => {}; }
    const q = searchParams.get('q') ?? '';
    if (!q.trim()) { setSearchResultEntries([]); return () => {}; }
    let cancelled = false;
    setSearchResultsLoading(true);
    api.faq.searchEntries(q.trim())
      .then(r => { if (!cancelled) { setSearchResultEntries(r); setSearchResultsLoading(false); } })
      .catch(() => { if (!cancelled) setSearchResultsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedKey, searchParams, api.faq]);

  // Nav sections (sidebar data)
  const navSections = useMemo(
    () => buildNavSections(sections, allCategories, categoryEntriesCache),
    [sections, allCategories, categoryEntriesCache],
  );

  const favouriteCount = useMemo(
    () => allCachedEntries.filter(e => favourites.has(e.id)).length,
    [allCachedEntries, favourites],
  );

  // Whether entries for the current category are still loading
  const isCategoryLoading = useMemo(() => {
    if (selectedKey === 'welcome' || selectedKey === 'favourites' || selectedKey === 'search') return false;
    const [sectionSlug, catSlug] = selectedKey.split('|');
    const section = sections.find(o => o.slug === sectionSlug);
    if (!section) return false;
    const cats = allCategories[section.id];
    if (!cats) return false;
    const cat = cats.find(c => toSlug(c.name) === catSlug);
    return cat ? categoryEntriesCache[cat.id] === undefined : false;
  }, [selectedKey, sections, allCategories, categoryEntriesCache]);

  // Entries to display in main area
  const visibleEntries = useMemo(() => {
    if (selectedKey === 'welcome' || selectedKey === 'search') return [];
    if (selectedKey === 'favourites') {
      const base = allCachedEntries.filter(e => favourites.has(e.id));
      const q = searchScope === 'filter' ? searchText.trim().toLowerCase() : '';
      if (!q) return base;
      return base.filter(e =>
        e.question.toLowerCase().includes(q) ||
        e.answer.toLowerCase().includes(q) ||
        (e.keywords ?? []).some((k: string) => k.toLowerCase().includes(q)),
      );
    }
    const [sectionSlug, catSlug] = selectedKey.split('|');
    const section = sections.find(o => o.slug === sectionSlug);
    if (!section) return [];
    const cats = allCategories[section.id];
    if (!cats) return [];
    const cat = cats.find(c => toSlug(c.name) === catSlug);
    if (!cat) return [];
    const entries = categoryEntriesCache[cat.id];
    if (!entries) return [];
    const q = searchScope === 'filter' ? searchText.trim().toLowerCase() : '';
    if (!q) return entries;
    return entries.filter(e =>
      e.question.toLowerCase().includes(q) ||
      e.answer.toLowerCase().includes(q) ||
      (e.keywords ?? []).some((k: string) => k.toLowerCase().includes(q)),
    );
  }, [selectedKey, categoryEntriesCache, allCachedEntries, sections, allCategories, searchText, searchScope, favourites]);

  // Section heading info
  const sectionInfo = useMemo(() => {
    if (selectedKey === 'welcome') return { title: 'Overview', color: undefined as string | undefined, sectionName: undefined as string | undefined, iconType: 'welcome' as const };
    if (selectedKey === 'favourites') return { title: 'Favourites', color: '#F59E0B' as string | undefined, sectionName: undefined as string | undefined, iconType: 'favourites' as const };
    if (selectedKey === 'search') return { title: 'Search Results', color: undefined as string | undefined, sectionName: undefined as string | undefined, iconType: 'welcome' as const };
    const [sectionSlug, catSlug] = selectedKey.split('|');
    const matchedSection = sections.find(o => o.slug === sectionSlug);
    const navSection = navSections.find(s => s.section.slug === sectionSlug);
    const catName = navSection?.categories.find(c => c.slug === catSlug)?.name ?? catSlug;
    return { title: catName, color: matchedSection?.color as string | undefined, sectionName: matchedSection?.name as string | undefined, iconType: 'category' as const };
  }, [selectedKey, sections, navSections]);

  // Group search results by section → category for the search results page
  const groupedSearchResults = useMemo(() => {
    if (selectedKey !== 'search') return [];
    const sectionMap = new Map<string, {
      sectionName: string; sectionColor: string; sectionSlug: string;
      categories: Map<string, FaqEntry[]>;
    }>();
    for (const entry of searchResultEntries) {
      if (!sectionMap.has(entry.sectionSlug)) {
        sectionMap.set(entry.sectionSlug, {
          sectionName: entry.sectionName,
          sectionColor: entry.sectionColor,
          sectionSlug: entry.sectionSlug,
          categories: new Map(),
        });
      }
      const sec = sectionMap.get(entry.sectionSlug)!;
      if (!sec.categories.has(entry.categoryName)) sec.categories.set(entry.categoryName, []);
      sec.categories.get(entry.categoryName)!.push(entry);
    }
    return [...sectionMap.values()].map(sec => ({
      sectionName: sec.sectionName,
      sectionColor: sec.sectionColor,
      sectionSlug: sec.sectionSlug,
      categories: [...sec.categories.entries()].map(([catName, entries]) => ({ catName, entries })),
    }));
  }, [selectedKey, searchResultEntries]);

  return (
    <Page themeId="tool">
      <Header
        title="Frequently Asked Questions"
        subtitle="Browse by category or search across all FAQs"
      />
      <Content>
        {loading && (
          <Box className={classes.centred}>
            <CircularProgress />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        )}
        {!loading && loadError && <Alert severity="error">{loadError}</Alert>}

        {!loading && !loadError && (
          <div className={classes.layout}>
            {/* ── Sidebar ── */}
            <Paper className={classes.sidebar} elevation={0}>
              <Typography component="span" className={classes.sidebarLabel}>
                Library
              </Typography>
              <List disablePadding className={classes.sidebarList}>
                {/* Overview */}
                <ListItem
                  disableGutters
                  className={`${classes.navItem} ${selectedKey === 'welcome' ? classes.navItemActive : ''}`}
                  onClick={() => setSelectedKey('welcome')}
                >
                  <ListItemIcon className={classes.navIcon}>
                    <HomeIcon className={selectedKey === 'welcome' ? classes.navIconActive : classes.navIconInactive} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Overview"
                    className={`${classes.navText} ${selectedKey === 'welcome' ? classes.navTextActive : ''}`}
                  />
                </ListItem>
                {/* Favourites */}
                <ListItem
                  disableGutters
                  className={`${classes.navItem} ${selectedKey === 'favourites' ? classes.navItemActive : ''}`}
                  onClick={() => setSelectedKey('favourites')}
                >
                  <ListItemIcon className={classes.navIcon}>
                    <StarIcon className={selectedKey === 'favourites' ? classes.navIconActive : classes.navIconStar} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Favourites"
                    className={`${classes.navText} ${selectedKey === 'favourites' ? classes.navTextActive : ''}`}
                  />
                  {favouriteCount > 0 && (
                    <span className={`${classes.badge} ${selectedKey === 'favourites' ? classes.badgeActive : ''}`}>
                      {favouriteCount}
                    </span>
                  )}
                </ListItem>
              </List>
              <Divider sx={{ mx: 1, my: 0.5 }} />
              <Typography component="span" className={classes.sidebarLabel}>
                Browse
              </Typography>
              <List disablePadding className={classes.sidebarList}>
                {navSections.map(section => (
                  <div key={section.section.id}>
                    {/* Section label */}
                    <Typography component="span" className={classes.sidebarLabel}>
                      <span
                        className={classes.sectionDot}
                        style={{ backgroundColor: section.section.color }}
                        aria-hidden="true"
                      />
                      {section.section.name}
                    </Typography>
                    {/* Category items */}
                    {section.categories.map(cat => {
                      const key = makeCatKey(section.section.slug, cat.slug);
                      const active = selectedKey === key;
                      return (
                        <ListItem
                          key={key}
                          disableGutters
                          className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
                          onClick={() => setSelectedKey(key)}
                        >
                          <ListItemText
                            primary={cat.name}
                            className={`${classes.navText} ${active ? classes.navTextActive : ''}`}
                          />
                          {cat.count !== undefined && cat.count > 0 && (
                            <span className={`${classes.badge} ${active ? classes.badgeActive : ''}`}>
                              {cat.count}
                            </span>
                          )}
                        </ListItem>
                      );
                    })}
                    <Divider sx={{ mx: 1, my: 0.5 }} />
                  </div>
                ))}
              </List>
            </Paper>

            {/* ── Main content ── */}
            <div className={classes.mainContent}>
              {/* Toolbar + section heading — shown only for category/favourites views */}
              {!detailEntry && selectedKey !== 'welcome' && selectedKey !== 'search' && (
                <>
                  {/* Toolbar */}
                  <div className={classes.toolbar}>
                    <div className={classes.searchWrap}>
                      <SearchIcon className={classes.searchIcon} />
                      <InputBase
                        className={classes.searchInput}
                        placeholder={getFilterPlaceholder(searchScope, selectedKey, sectionInfo.title)}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        inputProps={{ 'aria-label': 'filter questions' }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && searchScope === 'global' && searchText.trim()) navigateToSearch(searchText);
                        }}
                      />
                      {searchText && (
                        <IconButton size="small" style={{ padding: 2 }} onClick={() => setSearchText('')}>
                          <ClearIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </div>
                    {searchScope === 'global' && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={() => { if (searchText.trim()) navigateToSearch(searchText); }}
                        disabled={!searchText.trim()}
                        disableElevation
                      >
                        Search
                      </Button>
                    )}
                    {selectedKey !== 'favourites' && (
                      <div className={classes.searchScopeGroup}>
                        <Button
                          className={`${classes.searchScopeBtn}${searchScope === 'filter' ? ` ${classes.searchScopeBtnActive}` : ''}`}
                          disableRipple
                          onClick={() => { setSearchScope('filter'); setSearchText(''); }}
                        >
                          Filter
                        </Button>
                        <Button
                          className={`${classes.searchScopeBtn}${searchScope === 'global' ? ` ${classes.searchScopeBtnActive}` : ''}`}
                          disableRipple
                          onClick={() => { setSearchScope('global'); setSearchText(''); }}
                        >
                          Search all FAQs
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Section heading */}
                  <div className={classes.sectionHeader}>
                    <div
                      className={classes.sectionIconCircle}
                      style={{ backgroundColor: sectionInfo.color ? `${sectionInfo.color}18` : undefined }}
                    >
                      {sectionInfo.iconType === 'favourites' && (
                        <StarIcon style={{ fontSize: 22, color: '#F59E0B' }} />
                      )}
                      {sectionInfo.iconType === 'category' && (
                        <HelpOutlineIcon style={{ fontSize: 22, color: sectionInfo.color ?? '#757575' }} />
                      )}
                    </div>
                    <div>
                      <Typography className={classes.sectionTitle}>
                        {sectionInfo.title}
                      </Typography>
                      <Typography className={classes.sectionMeta}>
                        {sectionInfo.sectionName ? `${sectionInfo.sectionName} · ` : ''}
                        {visibleEntries.length} {visibleEntries.length === 1 ? 'question' : 'questions'}
                      </Typography>
                    </div>
                  </div>
                </>
              )}

              {/* Welcome overview page */}
              {selectedKey === 'welcome' && !detailEntry && (
                <FAQWelcome
                  recentEntries={recentEntries}
                  classes={classes}
                  onOpenEntry={openDetail}
                  onSearch={navigateToSearch}
                  favourites={favourites}
                  sections={sections}
                  allCategories={allCategories}
                  onToggleFavourite={handleToggleFavourite}
                />
              )}

              {/* Search results page */}
              {selectedKey === 'search' && !detailEntry && (
                <FAQSearchResults
                  query={searchParams.get('q') ?? ''}
                  groupedResults={groupedSearchResults}
                  loading={searchResultsLoading}
                  favourites={favourites}
                  classes={classes}
                  onOpenEntry={openDetail}
                  onToggleFavourite={handleToggleFavourite}
                  onSearch={navigateToSearch}
                  sections={sections}
                />
              )}

              {/* Card grid — shown for category/favourites views and detail overlay */}
              {((selectedKey !== 'welcome' && selectedKey !== 'search') || !!detailEntry) && (
                <>
                  {detailEntry && (
                    <EntryDetail
                      entry={fullDetailEntry ?? detailEntry!}
                      allEntries={allCachedEntries}
                      sectionColor={sections.find(o => o.slug === detailEntry.sectionSlug)?.color ?? '#757575'}
                      isFavourite={favourites.has(detailEntry.id)}
                      sections={sections}
                      favourites={favourites}
                      classes={classes}
                      onBack={closeDetail}
                      onToggleFavourite={() => handleToggleFavourite(detailEntry.id)}
                      onToggleFavouriteById={handleToggleFavourite}
                      onOpenEntry={openDetail}
                    />
                  )}
                  {!detailEntry && isCategoryLoading && (
                    <Box className={classes.centred}>
                      <CircularProgress size={32} />
                      <Typography variant="body2">Loading…</Typography>
                    </Box>
                  )}
                  {!detailEntry && !isCategoryLoading && visibleEntries.length === 0 && (
                    <Box className={classes.centred}>
                      <HelpOutlineIcon style={{ fontSize: 48, opacity: 0.3 }} />
                      <Typography variant="body1" style={{ fontWeight: 700 }}>
                        {searchText ? 'No matching questions' : 'No questions here yet'}
                      </Typography>
                      {searchText && (
                        <Typography variant="body2">
                          Try adjusting your search or browse a different category.
                        </Typography>
                      )}
                    </Box>
                  )}
                  {!detailEntry && !isCategoryLoading && visibleEntries.length > 0 && (
                    <div className={classes.cardGrid}>
                      {visibleEntries.map(entry => (
                        <EntryCard
                          key={entry.id}
                          entry={entry}
                          sectionColor={sections.find(o => o.slug === entry.sectionSlug)?.color ?? '#757575'}
                          isFavourite={favourites.has(entry.id)}
                          classes={classes}
                          onOpen={() => openDetail(entry)}
                          onToggleFavourite={() => handleToggleFavourite(entry.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Content>
    </Page>
  );
}

// ── Welcome / overview page ───────────────────────────────────────────────────

interface FAQWelcomeProps {
  recentEntries: FaqEntry[];
  classes: ReturnType<typeof useStyles>['classes'];
  onOpenEntry: (entry: FaqEntry) => void;
  onSearch: (q: string) => void;
  favourites: Set<string>;
  sections: FaqSection[];
  allCategories: Record<string, FaqCategory[]>;
  onToggleFavourite: (id: string) => void;
}

function FAQWelcome({ recentEntries, classes, onOpenEntry, onSearch, favourites, sections, allCategories, onToggleFavourite }: FAQWelcomeProps) {
  const [searchText, setSearchText] = useState('');

  const handleSearch = () => {
    const q = searchText.trim();
    if (!q) return;
    onSearch(q);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const totalCategories = Object.values(allCategories).reduce((acc, cats) => acc + cats.length, 0);
  const totalEntries = Object.values(allCategories).reduce(
    (acc, cats) => acc + cats.reduce((a, c) => a + (c.entryCount ?? 0), 0), 0,
  );

  return (
    <div className={classes.welcomeRoot}>

      {/* ── Search ── */}
      <div className={classes.welcomeSearchWrap}>
        <div className={classes.welcomeSearchBox}>
          <SearchIcon className={classes.welcomeSearchIcon} />
          <InputBase
            className={classes.welcomeSearchInput}
            placeholder="Search all FAQs…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            inputProps={{ 'aria-label': 'search all FAQs' }}
          />
          {searchText && (
            <IconButton size="small" style={{ padding: 2 }} onClick={() => setSearchText('')}>
              <ClearIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </div>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleSearch}
          disabled={!searchText.trim()}
          disableElevation
        >
          Search
        </Button>
      </div>

      {/* ── Stats bar ── */}
      {sections.length > 0 && (
        <>
          <Typography className={classes.welcomeRecentLabel} style={{ marginBottom: 8 }}>
            What&apos;s available
          </Typography>
          <div className={classes.welcomeStats}>
            <span className={classes.welcomeStatChip}>
              <strong>{sections.length}</strong> {sections.length === 1 ? 'section' : 'sections'}
            </span>
            <span className={classes.welcomeStatChip}>
              <strong>{totalCategories}</strong> {totalCategories === 1 ? 'category' : 'categories'}
            </span>
            <span className={classes.welcomeStatChip}>
              <strong>{totalEntries}</strong> {totalEntries === 1 ? 'FAQ' : 'FAQs'}
            </span>
          </div>
        </>
      )}

      {/* ── Recently updated cards ── */}
      {recentEntries.length > 0 && (
        <>
          <Typography className={classes.welcomeRecentLabel}>
            Recently updated
          </Typography>
          <div className={classes.cardGrid}>
            {recentEntries.map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                sectionColor={sections.find(o => o.slug === entry.sectionSlug)?.color ?? entry.sectionColor}
                isFavourite={favourites.has(entry.id)}
                classes={classes}
                onOpen={() => onOpenEntry(entry)}
                onToggleFavourite={() => onToggleFavourite(entry.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Search results page ───────────────────────────────────────────────────────

interface FAQSearchResultsProps {
  query: string;
  groupedResults: Array<{
    sectionName: string;
    sectionColor: string;
    sectionSlug: string;
    categories: Array<{ catName: string; entries: FaqEntry[] }>;
  }>;
  loading: boolean;
  favourites: Set<string>;
  classes: ReturnType<typeof useStyles>['classes'];
  onOpenEntry: (entry: FaqEntry) => void;
  onToggleFavourite: (id: string) => void;
  onSearch: (q: string) => void;
  sections: FaqSection[];
}

function FAQSearchResults({
  query, groupedResults, loading, favourites, classes, onOpenEntry, onToggleFavourite, onSearch, sections,
}: FAQSearchResultsProps) {
  const [searchText, setSearchText] = useState(query);

  const totalCount = groupedResults.reduce(
    (acc, sec) => acc + sec.categories.reduce((a, c) => a + c.entries.length, 0), 0,
  );

  const handleSearch = () => { if (searchText.trim()) onSearch(searchText); };

  if (loading) {
    return (
      <Box className={classes.centred}>
        <CircularProgress size={32} />
        <Typography variant="body2">Searching…</Typography>
      </Box>
    );
  }

  return (
    <div>
      {/* Search bar */}
      <div className={classes.welcomeSearchWrap}>
        <div className={classes.welcomeSearchBox}>
          <SearchIcon className={classes.welcomeSearchIcon} />
          <InputBase
            className={classes.welcomeSearchInput}
            placeholder="Search all FAQs…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            inputProps={{ 'aria-label': 'search all FAQs' }}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          {searchText && (
            <IconButton size="small" style={{ padding: 2 }} onClick={() => setSearchText('')}>
              <ClearIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </div>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleSearch}
          disabled={!searchText.trim()}
          disableElevation
        >
          Search
        </Button>
      </div>
      <div className={classes.searchResultsHeader}>
        <Typography className={classes.searchResultsCount}>
          {totalCount === 0
            ? `No results for "${query}"`
            : `${totalCount} ${totalCount === 1 ? 'result' : 'results'} for "${query}"`}
        </Typography>
      </div>

      {groupedResults.map(section => (
        <div key={section.sectionSlug} className={classes.searchResultsSectionGroup}>
          {/* Section header bar */}
          <div
            className={classes.searchResultsSectionBar}
            style={{ borderLeftColor: section.sectionColor }}
          >
            <span
              className={classes.sectionDot}
              style={{ backgroundColor: section.sectionColor }}
            />
            <Typography style={{ fontWeight: 700, fontSize: 13, color: section.sectionColor }}>
              {section.sectionName}
            </Typography>
            <Chip
              label={`${section.categories.reduce((a, c) => a + c.entries.length, 0)} result${section.categories.reduce((a, c) => a + c.entries.length, 0) === 1 ? '' : 's'}`}
              size="small"
              style={{
                backgroundColor: `${section.sectionColor}22`,
                color: section.sectionColor,
                fontWeight: 700,
                fontSize: 10,
                height: 20,
                marginLeft: 'auto',
              }}
            />
          </div>

          {section.categories.map(cat => (
            <div key={cat.catName} className={classes.searchResultsCategoryGroup}>
              <Typography className={classes.searchResultsCategoryLabel}>
                {cat.catName}
              </Typography>
              <div className={classes.cardGrid}>
                {cat.entries.map(entry => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    sectionColor={sections.find(o => o.slug === entry.sectionSlug)?.color ?? section.sectionColor}
                    isFavourite={favourites.has(entry.id)}
                    classes={classes}
                    onOpen={() => onOpenEntry(entry)}
                    onToggleFavourite={() => onToggleFavourite(entry.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      {!loading && totalCount === 0 && (
        <Box className={classes.centred}>
          <HelpOutlineIcon style={{ fontSize: 48, opacity: 0.3 }} />
          <Typography variant="body1" style={{ fontWeight: 700 }}>No matching FAQs found</Typography>
          <Typography variant="body2">Try different keywords or browse by category.</Typography>
        </Box>
      )}
    </div>
  );
}

// ── Entry card ────────────────────────────────────────────────────────────────

interface EntryCardProps {
  entry: FaqEntry;
  sectionColor: string;
  isFavourite: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  onOpen: () => void;
  onToggleFavourite: () => void;
}

function EntryCard({ entry, sectionColor, isFavourite, classes, onOpen, onToggleFavourite }: EntryCardProps) {
  const raw = stripMarkdown(entry.answer);
  const trimmed = raw.length > 120 ? raw.slice(0, 120).replace(/\s+\S*$/, '') : raw;
  const preview = trimmed ? `${trimmed}…` : '';

  return (
    <Card className={classes.entryCard} elevation={0}>
      {/* Top row: section chip + star */}
      <div className={classes.cardTopRow}>
        <Chip
          label={entry.sectionName}
          size="small"
          className={classes.cardSectionChip}
          style={{ backgroundColor: `${sectionColor}22`, color: sectionColor }}
        />
        <div style={{ flex: 1 }} />
        <Tooltip title={isFavourite ? 'Remove from favourites' : 'Add to favourites'} arrow>
          <IconButton
            size="small"
            className={classes.cardIconBtn}
            onClick={e => { e.stopPropagation(); onToggleFavourite(); }}
            aria-label={isFavourite ? 'unfavourite' : 'favourite'}
          >
            {isFavourite
              ? <StarIcon style={{ fontSize: 16, color: '#F59E0B' }} />
              : <StarBorderIcon style={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Clickable body */}
      <CardActionArea
        className={classes.cardActionArea}
        onClick={onOpen}
        component="div"
      >
        <Typography className={classes.cardQuestion}>
          {entry.question}
        </Typography>

        {preview && (
          <Typography className={classes.cardPreview}>
            {preview}
          </Typography>
        )}

        <div style={{ flex: 1 }} />

        {entry.keywords.length > 0 && (
          <>
            <Typography className={classes.tagsLabel}>Tags</Typography>
            <div className={classes.tagsWrap}>
              {entry.keywords.slice(0, 3).map((kw, i) => (
                <Chip key={i} label={kw} size="small" className={classes.tagChip} />
              ))}
              {entry.keywords.length > 3 && (
                <Tooltip
                  title={entry.keywords.slice(3).join(', ')}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={`+${entry.keywords.length - 3} more`}
                    size="small"
                    className={classes.tagChip}
                    style={{ cursor: 'default', opacity: 0.7 }}
                    onClick={e => e.stopPropagation()}
                  />
                </Tooltip>
              )}
            </div>
          </>
        )}

        <Typography className={classes.cardLastUpdated}>
          Updated {formatShortDate(entry.updatedAt)}
        </Typography>
      </CardActionArea>

      {/* Primary action */}
      <CardActions className={classes.cardActionsWrap}>
        <Button
          variant="contained"
          color="primary"
          className={classes.actionBtn}
          startIcon={<HelpOutlineIcon style={{ fontSize: 17 }} />}
          onClick={onOpen}
        >
          View FAQ
        </Button>
      </CardActions>
    </Card>
  );
}

// ── Entry detail (in-page) ────────────────────────────────────────────────────

interface EntryDetailProps {
  entry: FaqEntry;
  allEntries: FaqEntry[];
  sectionColor: string;
  isFavourite: boolean;
  sections: FaqSection[];
  favourites: Set<string>;
  classes: ReturnType<typeof useStyles>['classes'];
  onBack: () => void;
  onToggleFavourite: () => void;
  onToggleFavouriteById: (id: string) => void;
  onOpenEntry: (entry: FaqEntry) => void;
}

function EntryDetail({ entry, allEntries, sectionColor, isFavourite, sections, favourites, classes, onBack, onToggleFavourite, onToggleFavouriteById, onOpenEntry }: EntryDetailProps) {
  const api = useApi(oneadvancedApiRef);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<{ helpful: number; unhelpful: number; userVote: boolean | null } | null>(null);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  // Load feedback counts when entry changes
  useEffect(() => {
    let cancelled = false;
    api.faq.getFeedback(entry.id).then(fb => {
      if (!cancelled) setFeedback(fb);
    }).catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [api.faq, entry.id]);

  const handleVote = useCallback(async (helpful: boolean) => {
    try {
      const updated = await api.faq.submitFeedback(entry.id, helpful);
      setFeedback(updated);
    } catch { /* non-critical */ }
  }, [api.faq, entry.id]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const related = useMemo(() => getRelated(entry, allEntries), [entry, allEntries]);

  return (
    <div>
      {/* Back button */}
      <Button
        className={classes.detailBack}
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBack}
        size="small"
      >
        Back to FAQs
      </Button>

      {/* Single seamless card */}
      <Paper
        className={classes.detailCard}
        elevation={0}
      >
        {/* ── Header section ── */}
        <div className={classes.detailCardHeader} style={{ borderTopColor: sectionColor }}>
          {/* Title row — question + copy link + favourite toggle */}
          <div className={classes.detailTitleRow}>
            <Typography className={classes.detailQuestion}>
              {entry.question}
            </Typography>
            <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
              <IconButton
                size="small"
                onClick={handleCopyLink}
                aria-label="copy link"
                style={{ flexShrink: 0, alignSelf: 'flex-start' }}
              >
                {copied
                  ? <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />
                  : <ContentCopyIcon style={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}>
              <IconButton
                size="small"
                onClick={onToggleFavourite}
                aria-label={isFavourite ? 'unfavourite' : 'favourite'}
                style={{ flexShrink: 0, alignSelf: 'flex-start' }}
              >
                {isFavourite
                  ? <StarIcon style={{ color: '#F59E0B' }} />
                  : <StarBorderIcon />}
              </IconButton>
            </Tooltip>
          </div>

          {/* Meta grid */}
          <div className={classes.detailMeta}>
            <div>
              <Typography className={classes.detailMetaLabel}>Section</Typography>
              <Typography className={classes.detailMetaValue}>{entry.sectionName}</Typography>
            </div>
            <div>
              <Typography className={classes.detailMetaLabel}>Category</Typography>
              <Typography className={classes.detailMetaValue}>{entry.categoryName}</Typography>
            </div>
            <div>
              <Typography className={classes.detailMetaLabel}>Created</Typography>
              <Typography className={classes.detailMetaValue}>{fmt(entry.createdAt)}</Typography>
            </div>
            <div>
              <Typography className={classes.detailMetaLabel}>Last updated</Typography>
              <Typography className={classes.detailMetaValue}>{fmt(entry.updatedAt)}</Typography>
            </div>
          </div>

          {entry.keywords.length > 0 && (
            <>
              <Typography className={classes.detailMetaLabel}>Tags</Typography>
              <div className={classes.detailTags}>
                {entry.keywords.map((kw, i) => (
                  <Chip key={i} label={kw} size="small" className={classes.tagChip} />
                ))}
              </div>
            </>
          )}
        </div>

        <Divider />

        {/* ── Answer section ── */}
        <div className={classes.detailCardBody}>
          <Typography className={classes.detailSectionTitle}>Answer</Typography>
          <div className={classes.answerContent}>
            <MarkdownContent content={entry.answer} dialect="gfm" />
          </div>
        </div>

        {/* ── Link buttons ── */}
        {entry.links.length > 0 && (
          <>
            <Divider />
            <div style={{ padding: '16px 24px 16px' }}>
              <Typography className={classes.detailCardLinksLabel}>Links</Typography>
              <div className={classes.detailCardLinks} style={{ padding: 0 }}>
                {entry.links.map((link, i) => (
                  <Button
                    key={i}
                    variant="outlined"
                    size="small"
                    startIcon={<LinkIcon type={link.type} />}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </Button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Was this helpful? ── */}
        <div className={classes.feedbackSection}>
          <Typography className={classes.feedbackLabel}>Was this helpful?</Typography>
          <Button
            size="small"
            variant={feedback?.userVote === true ? 'contained' : 'outlined'}
            color="primary"
            className={classes.feedbackBtn}
            onClick={() => handleVote(true)}
            startIcon={feedback?.userVote === true ? <ThumbUpAltIcon fontSize="small" /> : <ThumbUpAltOutlinedIcon fontSize="small" />}
          >
            Yes{feedback && feedback.helpful > 0 ? ` (${feedback.helpful})` : ''}
          </Button>
          <Button
            size="small"
            variant={feedback?.userVote === false ? 'contained' : 'outlined'}
            color="inherit"
            className={classes.feedbackBtn}
            onClick={() => handleVote(false)}
            startIcon={feedback?.userVote === false ? <ThumbDownAltIcon fontSize="small" /> : <ThumbDownAltOutlinedIcon fontSize="small" />}
          >
            No{feedback && feedback.unhelpful > 0 ? ` (${feedback.unhelpful})` : ''}
          </Button>
        </div>

        {/* ── Related FAQs ── */}
        {related.length > 0 && (
          <div className={classes.relatedSection}>
            <Typography className={classes.relatedTitle}>Related FAQs</Typography>
            <div className={classes.cardGrid}>
              {related.map(rel => (
                <EntryCard
                  key={rel.id}
                  entry={rel}
                  sectionColor={sections.find(s => s.slug === rel.sectionSlug)?.color ?? sectionColor}
                  isFavourite={favourites.has(rel.id)}
                  classes={classes}
                  onOpen={() => onOpenEntry(rel)}
                  onToggleFavourite={() => onToggleFavouriteById(rel.id)}
                />
              ))}
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
}
